import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Mic, MicOff } from 'lucide-react';
import type { Editor } from '@tiptap/react';
import type { Transaction } from '@tiptap/pm/state';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components';
import { toInlineTextContent } from 'components/richText';
import { type TranscriptMessage, useDictation } from 'hooks/useDictation';
import {
  AudioBar,
  AudioLevelIndicator,
  DictationActions,
  MicButton,
  MicOffButton,
} from './NoteDictation.styles';

interface NoteDictationProps {
  editor: Editor;
  section: string;
  label: string;
  dictatingSection?: string;
  onDictatingChange: (section: string, isDictating: boolean) => void;
}

export const NoteDictation = ({
  editor,
  section,
  label,
  dictatingSection,
  onDictatingChange,
}: NoteDictationProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pendingInsertRef = useRef<{ from: number; length: number } | null>(null);

  useEffect(() => {
    const handleTransaction = ({ transaction }: { transaction: Transaction }) => {
      const pending = pendingInsertRef.current;
      if (!pending) return;
      const from = transaction.mapping.map(pending.from);
      const to = transaction.mapping.map(pending.from + pending.length);
      pendingInsertRef.current = { from, length: Math.max(0, to - from) };
    };
    editor.on('transaction', handleTransaction);
    return () => {
      editor.off('transaction', handleTransaction);
    };
  }, [editor]);

  const insertTranscript = useCallback(
    ({ text, isFinal }: TranscriptMessage) => {
      const pending = pendingInsertRef.current;
      const from = pending?.from ?? editor.state.selection.from;
      const to = pending ? from + pending.length : from;
      editor.chain().insertContentAt({ from, to }, toInlineTextContent(text)).run();
      if (isFinal) {
        pendingInsertRef.current = null;
        editor
          .chain()
          .insertContentAt(from + text.length, ' ')
          .run();
      } else {
        pendingInsertRef.current = { from, length: text.length };
      }
    },
    [editor]
  );

  const { audioLevel, activeDictation, start, initiateStop } = useDictation(
    insertTranscript,
    section,
    { onNoDevice: () => void navigate('/audio-settings') }
  );
  const isDictating = activeDictation === section;

  useEffect(() => {
    onDictatingChange(section, isDictating);
  }, [section, isDictating, onDictatingChange]);

  const startDictation = () => {
    if (!editor.isFocused) editor.commands.setTextSelection(editor.state.doc.content.size);
    void start();
  };

  const stopDictation = async () => {
    await initiateStop();
    pendingInsertRef.current = null;
  };

  if (isDictating) {
    return (
      <DictationActions>
        <AudioLevelIndicator>
          <AudioBar style={{ height: `${Math.max(2, Math.round(audioLevel * 12))}px` }} />
          <AudioBar style={{ height: `${Math.max(2, Math.round(audioLevel * 16))}px` }} />
          <AudioBar style={{ height: `${Math.max(2, Math.round(audioLevel * 12))}px` }} />
        </AudioLevelIndicator>
        <MicOffButton
          aria-label={t('dictation.stop', { section: label })}
          onClick={() => void stopDictation()}
        >
          <MicOff className="h-4 w-4" />
        </MicOffButton>
      </DictationActions>
    );
  }

  const isBlocked = dictatingSection !== undefined;
  const micButton = (
    <MicButton
      aria-label={t('dictation.start', { section: label })}
      disabled={isBlocked}
      onMouseDown={(event) => event.preventDefault()}
      onClick={startDictation}
    >
      <Mic className="h-4 w-4" />
    </MicButton>
  );

  if (!isBlocked) return micButton;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{micButton}</TooltipTrigger>
      <TooltipContent>{t('result.dictation.disabled')}</TooltipContent>
    </Tooltip>
  );
};
